import Link from "next/link";
import { usePathname } from "next/navigation";
import { useRouter } from "next13-progressbar";
import { CSSProperties } from "react";
import { useTranslations } from "next-intl";

interface NavLinkProps {
  name: string;
  style?: CSSProperties;
  nameToUse?: string;
  showBottomBorder?: boolean;
  queryParams?: string;
  skipToID?: string;
}

export const NavLink = ({
  name,
  style = {},
  nameToUse,
  showBottomBorder,
  queryParams,
  skipToID,
  ...props
}: NavLinkProps) => {
  const t = useTranslations("NavigationBar");
  const router = useRouter();
  const pathName = decodeURIComponent(usePathname() || "");
  const href = `/${nameToUse ?? name}${queryParams ?? ""}`;
  const targetBase = `/${nameToUse ?? name}`.toLowerCase();
  const isActiveNavlink =
    pathName === targetBase ||
    pathName.startsWith(`${targetBase}/`) ||
    pathName === `/${decodeURIComponent(name)}` ||
    showBottomBorder;

  const handleSkipBehavior = (e: React.SyntheticEvent) => {
    if (isActiveNavlink && skipToID) {
      e.preventDefault();
      const target = document.getElementById(skipToID);
      if (target) {
        target.focus();
      }
      return true;
    }
    return false;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === " ") {
      if (handleSkipBehavior(e)) return;

      e.preventDefault();

      router.push(href);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (handleSkipBehavior(e)) return;
  };

  return (
    <Link
      onClick={handleClick}
      href={href}
      onKeyDown={handleKeyDown}
      style={style}
      aria-current={isActiveNavlink ? "page" : undefined}
      aria-label={
        isActiveNavlink && skipToID
          ? t("ariaActivePage", {
              currentPage: decodeURIComponent(name),
            })
          : `${decodeURIComponent(name)},`
      }
      {...props}
      className={[
        "capitalize",
        "leading-6",
        "text-black",
        "px-4",
        "hover:bg-gray-50",
        "text-center",
        "pb-3",
        "text-[16px]",
        "flex",
        "items-center",
        "sm:pt-2",
        `${isActiveNavlink ? "sm:pb-4" : "sm:pb-5"}`,
        "sm:px-4",
        `link ${isActiveNavlink ? "border-b-4" : ""}`,
      ].join(" ")}
    >
      {decodeURIComponent(name)}
    </Link>
  );
};
